import Swal from "sweetalert2";
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Spinner } from 'react-bootstrap';
import UsersDropDown from './UsersDropDown';
import Badge from '@/shared/components/Badge';
import DeleteIcon from 'mdi-react/DeleteIcon';
import Modal from '@/shared/components/Modal';
import React, { useState, useEffect } from 'react';
import AdminInterface from '@/utils/AdminInterface';
import withReactContent from "sweetalert2-react-content";
import { paddingLeft, paddingRight } from '@/utils/directions';
import ReactTableBase from '@/shared/components/table/ReactTableBase';
import { CardBody, CardTitleWrap, CardTitle } from '@/shared/components/Card';
import { colorAdditional, colorGreenHover, colorAccent } from '@/utils/palette';

const CartTableButton = styled.button`
  background: transparent;
  border: none;
  line-height: 14px;
  ${paddingLeft}: 20px;
  ${paddingRight}: 0;
  padding-top: 0;
  padding-bottom: 0;
  position: relative;
  color: ${colorAdditional};
  cursor: pointer;
  transition: all 0.3s;

  svg {
    height: 20px;
    width: 20px;
    fill: ${colorAdditional};
    transition: all 0.3s;
  }

  &:hover {
    color: ${colorGreenHover};

    svg {
      fill: ${colorGreenHover};
    }
  }
`;

const TableSpinner = styled(Spinner)`
  color: ${colorAccent};
`;

const StatusFormatter = value => (
    value ? <Badge bg="success">Enable</Badge> : <Badge bg="secondary">Disable</Badge>
);

StatusFormatter.propTypes = {
    value: PropTypes.bool.isRequired,
};

const EditUserLicense = (props) => {
    const [showLoader, setShowLoader] = useState(true);
    const [listRequiresReload, setListRequiresReload] = useState(true);
    const [licenseUsers, setLicenseUsers] = useState({ tableHeaderData: [], tableRowsData: [] });
    const tableConfig = {
        isEditable: false,
        isSortable: true,
        isResizable: false,
        withPagination: false,
        withSearchEngine: false
    };

    const handleDelete = (license_id, user_id) => {
        Swal.fire({
            title: 'Warning!',
            text: `Do you want to remove accessing to "${props.selectedLicense.name}" for user "${user_id}"?`,
            icon: 'warning',
            confirmButtonText: 'Yes!',
            cancelButtonText: 'No!',
            showCancelButton: true
        }).then((result) => {
            if (result.isConfirmed) {
                AdminInterface.instance.deleteLicenseUser(license_id, user_id).then(() => {
                    withReactContent(Swal).fire({
                        icon: "success",
                        title: "Success",
                        html: "License access is removed!",
                    });
                }).catch((error) => {
                    withReactContent(Swal).fire({
                        icon: "error",
                        title: "Error",
                        html: error.response,
                    });
                }).finally(() => {
                    setListRequiresReload(true);
                });
            }
        });
    }

    const GetLicenseUsers = () => {
        AdminInterface.instance.getLicenseUsers(props.selectedLicense.id).then((response) => {
            const columns = [
                {
                    accessor: 'user_id',
                    Header: 'User',
                },
                {
                    accessor: 'creation_datetime',
                    Header: 'Creation',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'expiration_datetime',
                    Header: 'Expiration',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'delete',
                    Header: 'Delete',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                }
            ];
            const data = [];
            response.data.forEach(userLicense => {
                data.push({
                    user_id: userLicense.user ? `${userLicense.user.given_name} ${userLicense.user.family_name} (...${userLicense.user_id.slice(-5)})` : userLicense.user_id,
                    creation_datetime: new Date(`${userLicense.creation_datetime}z`).toLocaleString(),
                    expiration_datetime: new Date(`${userLicense.expiration_datetime}z`).toLocaleString(),
                    delete: <CartTableButton type="button" onClick={() => handleDelete(props.selectedLicense.id, userLicense.user_id)}><DeleteIcon /></CartTableButton>,
                });
            });

            setLicenseUsers({ tableHeaderData: columns, tableRowsData: data });
        }).catch((error) => {
            withReactContent(Swal).fire({
                title: "Error",
                html: error.response?.data?.detail,
            });
        }).finally(() => {
            setShowLoader(false);
            setListRequiresReload(false);
        });
    }

    useEffect(() => {
        GetLicenseUsers();
    }, [props.selectedLicense, listRequiresReload]);

    return (
        <CardBody>
            <CardTitleWrap>
                <CardTitle>Users having access to "{props.selectedLicense.name}"</CardTitle>
            </CardTitleWrap>
            {showLoader && <div className="text-center"><TableSpinner animation="border" /></div>}
            {!showLoader &&
                <>
                    <Modal color="primary"
                        title="Grant access to the license" 
                        confirmBtn={false} 
                        cancelBtnTitle={"Done"} 
                        btn="Add User" 
                        size="sm" 
                        isMainButtonDisabled={props.selectedLicense.is_open_data}
                        message={<UsersDropDown selectedLicense={props.selectedLicense} setListRequiresReload={setListRequiresReload} />} />
                    <ReactTableBase columns={licenseUsers.tableHeaderData} data={licenseUsers.tableRowsData} tableConfig={tableConfig} />
                </>
            }
        </CardBody>
    );
};

export default EditUserLicense;